/**
 * tkns: balance + purchase flow against the 30-payments worker.
 *
 * Buying: create the order server-side (client only ever sends the pack id),
 * open Revolut checkout in a new tab, then poll the refresh endpoint until
 * the webhook (or the poll itself) has credited the tkns.
 */
import { clientConfig, notify, text } from "@quickpoint.me/shared";
import { getAuth } from "firebase/auth";
import { useCallback, useRef } from "react";
import { useSetRecoilState } from "recoil";
import { TknsBalanceAtom, TknsMetersAtom, TknsTierAtom } from "../state/tknsAtoms";

export interface TknsPack {
	id: string;
	name: string;
	tkns: number;
	amountMinor: number;
	currency: string;
	blurb: string;
}

export interface PriceTable {
	/** absent while packs are withheld (subs-first strategy) */
	packs?: TknsPack[];
	subscription: { id: string; name: string; amountMinor: number; monthlyTkns: number; dailyTkns: number; storageMB: number; blurb: string };
	freeTier: { presentations: number; storageMB: number; publishedStories: number; monthlyTkns: number; dailyTkns: number };
	taskCost: { CHAT: number; IMAGE: number };
	/** false until the worker has a Revolut plan configured */
	subscriptionsEnabled?: boolean;
}

export interface TknsMeters {
	today: { used: number; cap: number };
	month: { used: number; grant: number };
}

// the price table is static per deploy — fetch once per session
let priceTablePromise: Promise<PriceTable> | null = null;
export function fetchPriceTable(): Promise<PriceTable> {
	if (!priceTablePromise) {
		priceTablePromise = fetch(`${clientConfig.payments()}/packs`).then((r) => {
			if (!r.ok) throw new Error(`packs: ${r.status}`);
			return r.json();
		});
		// a transient failure shouldn't poison the whole session
		priceTablePromise.catch(() => {
			priceTablePromise = null;
		});
	}
	return priceTablePromise;
}

/** Opens a URL from a user gesture; anchor fallback beats popup blockers. */
function openCheckout(url: string): void {
	const popup = window.open(url, "_blank");
	if (!popup) {
		const a = document.createElement("a");
		a.href = url;
		a.target = "_blank";
		a.rel = "noopener";
		document.body.appendChild(a);
		a.click();
		a.remove();
	}
}

const POLL_INTERVAL_MS = 3000;
const POLL_ATTEMPTS = 60; // ~3 minutes of patience for the checkout

export function useTkns() {
	const setBalance = useSetRecoilState(TknsBalanceAtom);
	const setMeters = useSetRecoilState(TknsMetersAtom);
	const setTier = useSetRecoilState(TknsTierAtom);
	const pollTimer = useRef<ReturnType<typeof setTimeout>>();

	const refreshBalance = useCallback(async (): Promise<{ balance: number; tier: "free" | "plus" } | null> => {
		const jwt = await getAuth().currentUser?.getIdToken();
		if (!jwt) {
			setBalance(null);
			return null;
		}
		try {
			const res = await fetch(`${clientConfig.payments()}/balance`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ jwt }),
			});
			if (!res.ok) throw new Error(`balance: ${res.status}`);
			const data = (await res.json()) as { balance: number; tier?: "free" | "plus" } & TknsMeters;
			setBalance(data.balance);
			setMeters({ today: data.today, month: data.month });
			setTier(data.tier ?? "free");
			return { balance: data.balance, tier: data.tier ?? "free" };
		} catch (e) {
			console.error("tkns balance", e);
			return null;
		}
	}, [setBalance, setMeters, setTier]);

	/**
	 * Creates the order and opens checkout. Resolves once the purchase is
	 * credited (balance updated) or the polling window closes.
	 */
	const buyPack = useCallback(
		async (pack: TknsPack): Promise<"credited" | "timeout" | "error"> => {
			const jwt = await getAuth().currentUser?.getIdToken();
			if (!jwt) return "error";

			let orderId: string;
			try {
				const res = await fetch(`${clientConfig.payments()}/orders`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ jwt, packId: pack.id }),
				});
				if (!res.ok) throw new Error(`orders: ${res.status}`);
				const order = (await res.json()) as { orderId: string; checkoutUrl?: string };
				orderId = order.orderId;
				if (!order.checkoutUrl) throw new Error("no checkout url");
				openCheckout(order.checkoutUrl);
			} catch (e) {
				console.error("tkns order", e);
				notify(text.tkns.loadError);
				return "error";
			}

			// poll until the webhook (or this refresh call) credits the order
			clearTimeout(pollTimer.current);
			return new Promise((resolve) => {
				let attempts = 0;
				const poll = async () => {
					attempts++;
					try {
						const freshJwt = await getAuth().currentUser?.getIdToken();
						const res = await fetch(`${clientConfig.payments()}/orders/${orderId}/refresh`, {
							method: "POST",
							headers: { "Content-Type": "application/json" },
							body: JSON.stringify({ jwt: freshJwt }),
						});
						if (res.ok) {
							const r = (await res.json()) as { credited: boolean; balance: number };
							if (r.credited) {
								setBalance(r.balance);
								notify(text.tkns.purchased(pack.tkns));
								resolve("credited");
								return;
							}
						}
					} catch {
						// transient — keep polling
					}
					if (attempts >= POLL_ATTEMPTS) {
						notify(text.tkns.purchaseTimeout, { variant: "info" });
						resolve("timeout");
						return;
					}
					pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
				};
				pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
			});
		},
		[setBalance],
	);

	/**
	 * Starts the Plus subscription: opens the Revolut setup-order checkout,
	 * then polls the balance until the webhook flips the tier to plus.
	 */
	const subscribe = useCallback(async (): Promise<"subscribed" | "timeout" | "error"> => {
		const jwt = await getAuth().currentUser?.getIdToken();
		if (!jwt) return "error";
		try {
			const res = await fetch(`${clientConfig.payments()}/subscribe`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ jwt }),
			});
			if (!res.ok) throw new Error(`subscribe: ${res.status}`);
			const data = (await res.json()) as { checkoutUrl?: string; already?: boolean };
			if (data.already) {
				await refreshBalance();
				notify(text.tkns.subscribed);
				return "subscribed";
			}
			if (!data.checkoutUrl) throw new Error("no checkout url");
			openCheckout(data.checkoutUrl);
		} catch (e) {
			console.error("tkns subscribe", e);
			notify(text.tkns.loadError);
			return "error";
		}

		clearTimeout(pollTimer.current);
		return new Promise((resolve) => {
			let attempts = 0;
			const poll = async () => {
				attempts++;
				const r = await refreshBalance();
				if (r?.tier === "plus") {
					notify(text.tkns.subscribed);
					// the tier flips a beat before the TKNS grant lands — one
					// trailing refresh so the shown balance includes it
					setTimeout(() => refreshBalance(), 2500);
					resolve("subscribed");
					return;
				}
				if (attempts >= POLL_ATTEMPTS) {
					notify(text.tkns.purchaseTimeout, { variant: "info" });
					resolve("timeout");
					return;
				}
				pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
			};
			pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
		});
	}, [refreshBalance]);

	/** Cancels Plus at Revolut and drops to free immediately; balance kept. */
	const cancelSubscription = useCallback(async (): Promise<boolean> => {
		const jwt = await getAuth().currentUser?.getIdToken();
		if (!jwt) return false;
		try {
			const res = await fetch(`${clientConfig.payments()}/subscribe/cancel`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ jwt }),
			});
			if (!res.ok) throw new Error(`cancel: ${res.status}`);
			await refreshBalance();
			notify(text.tkns.cancelSubDone);
			return true;
		} catch (e) {
			console.error("tkns cancel", e);
			notify(text.tkns.loadError);
			return false;
		}
	}, [refreshBalance]);

	return { refreshBalance, buyPack, subscribe, cancelSubscription };
}
